import { SessionService } from './SessionService';
import { ENV } from '../environments/environment';
import ApiService from './ApiService';
import SecureStorageService from './SecureStorageService';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'radiologist' | 'technician' | 'viewer';
  permissions: string[];
  lastLogin: string;
  failedLoginAttempts: number;
  accountLocked: boolean;
  accountLockedUntil?: string;
}

interface LoginResponse {
  is_success: boolean;
  status: number;
  message: string;
  access_token: string;
  refresh_token?: string;
  idle_expiry?: number;
  refresh_expiry?: number;
  data: any[];
}

const FORCE_PASSWORD_CHANGE_KEY = 'force_password_change';
const RELOGIN_REQUIRED_KEY = 'relogin_required';
const IS_FIRST_TIME_LOGIN_KEY = 'is_first_time_login';

interface LoginAttempt {
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  success: boolean;
  failureReason?: string;
}

interface UserRoleResponse {
  is_success: boolean;
  status: number;
  message: string;
  data: {
    id: string;
    username: string;
    email: string;
    role: string;
    permissions?: string[];
    [key: string]: any;
  }[];
}

class AuthService {
  private static instance: AuthService;
  private currentUser: User | null = null;
  private sessionService: SessionService;
  private apiService: ApiService;
  private loginAttempts: LoginAttempt[] = [];
  private readonly MAX_LOGIN_ATTEMPTS = 5;
  private readonly LOCKOUT_DURATION_MINUTES = 15;
  private readonly SESSION_TIMEOUT_MINUTES = 30;

  // Demo users - In production, this would come from a secure database
  private readonly DEMO_USERS: User[] = [
    {
      id: '1',
      username: 'admin',
      email: 'admin@ohif.org',
      role: 'admin',
      permissions: ['*'],
      lastLogin: '',
      failedLoginAttempts: 0,
      accountLocked: false,
    },
    {
      id: '2',
      username: 'radiologist',
      email: 'radiologist@ohif.org',
      role: 'radiologist',
      permissions: ['patient/*.read', 'study/*.read', 'series/*.read'],
      lastLogin: '',
      failedLoginAttempts: 0,
      accountLocked: false,
    },
    {
      id: '3',
      username: 'technician',
      email: 'technician@ohif.org',
      role: 'technician',
      permissions: ['study/*.read', 'series/*.read'],
      lastLogin: '',
      failedLoginAttempts: 0,
      accountLocked: false,
    },
  ];

  private secureStorage: SecureStorageService;

  private constructor() {
    this.sessionService = SessionService.getInstance({
      timeoutMinutes: this.SESSION_TIMEOUT_MINUTES,
      warningMinutes: 5,
      enableAuditLogging: true,
    });

    this.apiService = ApiService.getInstance();
    this.secureStorage = SecureStorageService.getInstance();

    // Set up session timeout callback
    this.sessionService.setOnTimeoutCallback(() => {
      this.handleSessionTimeout();
    });

    // Initialize encryption and migrate existing data
    this.initializeSecureStorage();

    // Check for existing session on initialization
    // Store the promise so we can wait for it if needed
    this.sessionCheckPromise = this.checkExistingSession();
  }

  /**
   * Initializes secure storage and migrates existing unencrypted data
   */
  private async initializeSecureStorage(): Promise<void> {
    try {
      // Initialize and verify encryption is working
      const encryptionReady = await this.secureStorage.initializeEncryption();
      if (!encryptionReady) {
        console.warn('[AuthService] Encryption is not available (likely HTTP environment) - using unencrypted storage');
        // This is normal for HTTP environments - continue without encryption
      }

      // Migrate any existing unencrypted sensitive data
      // This will only encrypt if encryption is available, otherwise it will keep data unencrypted
      await this.secureStorage.migrateAllSensitiveData();
    } catch (error) {
      console.warn('[AuthService] Failed to initialize secure storage (will use unencrypted storage):', error);
      // Continue without encryption - this is acceptable for HTTP environments
    }
  }

  private sessionCheckPromise: Promise<void> | null = null;

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Waits for the session check to complete
   * Useful when you need to ensure session is restored before checking authentication
   */
  public async waitForSessionCheck(): Promise<void> {
    if (this.sessionCheckPromise) {
      return this.sessionCheckPromise;
    }
    // If checkExistingSession hasn't been called yet, call it now
    if (!this.sessionCheckPromise) {
      this.sessionCheckPromise = this.checkExistingSession();
    }
    return this.sessionCheckPromise;
  }

  private async checkExistingSession(): Promise<void> {
    try {
      // console.log('[AuthService] Starting session restoration...');

      // First, ensure encryption is initialized (this will derive the key if tokens are available)
      try {
        const encryptionReady = await this.secureStorage.isEncryptionAvailable();
        if (!encryptionReady) {
          console.warn('[AuthService] Encryption not ready, attempting to initialize...');
          await this.secureStorage.initializeEncryption();
        }
      } catch (encError) {
        console.warn('[AuthService] Encryption initialization warning:', encError);
        // Continue anyway - might be able to use unencrypted data
      }

      // Proactively check for old encrypted data that might cause issues
      // If we have encrypted data but no matching unencrypted data, and we have a token hash,
      // we should clear the old encrypted data before trying to decrypt
      const hasEncryptedSession = !!sessionStorage.getItem('encrypted_ohif_session');
      const hasUnencryptedSession = !!sessionStorage.getItem('ohif_session');
      const hasTokenHash = !!(sessionStorage.getItem('_auth_token_hash') ||
                             sessionStorage.getItem('_refresh_token_hash') ||
                             localStorage.getItem('_token_hash'));

      if (hasEncryptedSession && !hasUnencryptedSession && hasTokenHash) {
        // We have encrypted data, no unencrypted fallback, and a token hash
        // Try to decrypt a test value to see if the key works
        // If it fails, we'll clear all old data
        try {
          const testEncrypted = sessionStorage.getItem('encrypted_ohif_session');
          if (testEncrypted) {
            // Just check if we can parse it - actual decryption will happen in getSessionItem
            // But if we can't even get the key, we know there's a problem
            await this.secureStorage.isEncryptionAvailable();
          }
        } catch (testError) {
          console.warn('[AuthService] Detected potential key mismatch, clearing old encrypted data proactively');
          // Clear all old encrypted data
          const encryptedKeys = ['encrypted_ohif_session', 'encrypted_auth_token', 'encrypted_refresh_token', 'encrypted_idle_expiry'];
          encryptedKeys.forEach(key => sessionStorage.removeItem(key));
        }
      }

      // Try to get session data - first try encrypted, then fallback to unencrypted
      let sessionData: string | null = null;

      try {
        sessionData = await this.secureStorage.getSessionItem('ohif_session');
        // console.log('[AuthService] Retrieved session from encrypted storage:', sessionData ? 'found' : 'not found');
      } catch (error) {
        console.warn('[AuthService] Failed to get encrypted session, trying unencrypted:', error);

        // If decryption failed due to key mismatch, clear all encrypted data
        // This handles the case where old encrypted data exists but can't be decrypted
        if (error instanceof Error && error.message.includes('key mismatch')) {
          console.warn('[AuthService] Clearing old encrypted data that cannot be decrypted');
          try {
            // Clear all encrypted session items
            const encryptedKeys = ['encrypted_ohif_session', 'encrypted_auth_token', 'encrypted_refresh_token', 'encrypted_idle_expiry'];
            encryptedKeys.forEach(key => sessionStorage.removeItem(key));
            // Also clear token hashes to force re-generation
            sessionStorage.removeItem('_auth_token_hash');
            sessionStorage.removeItem('_refresh_token_hash');
            localStorage.removeItem('_token_hash');
          } catch (clearError) {
            console.warn('[AuthService] Error clearing old encrypted data:', clearError);
          }
        }

        // Fallback to unencrypted for migration or if encryption fails
        sessionData = sessionStorage.getItem('ohif_session');
        if (sessionData) {
          // console.log('[AuthService] Found unencrypted session, attempting migration...');
          try {
            // Try to migrate to encrypted storage (non-blocking)
            await this.secureStorage.migrateToEncrypted('ohif_session', false);
            // console.log('[AuthService] Session migrated to encrypted storage');
            // Re-fetch the encrypted session
            try {
              sessionData = await this.secureStorage.getSessionItem('ohif_session');
            } catch (e) {
              // Keep using unencrypted if re-fetch fails
              console.warn('[AuthService] Could not re-fetch encrypted session, using unencrypted');
            }
          } catch (migrateError) {
            // Migration failed, but continue with unencrypted data
            console.warn('AuthService: Failed to migrate session to encrypted storage:', migrateError);
          }
        }
      }

      if (sessionData) {
        try {
          const session = JSON.parse(sessionData);
          const now = Date.now();
          // console.log('[AuthService] Session expires at:', new Date(session.expiresAt).toISOString());
          // console.log('[AuthService] Current time:', new Date(now).toISOString());

          // Check if session is still valid
          if (session.expiresAt > now) {
            // Validate that session.user has required fields
            if (!session.user || !session.user.role || !session.user.username) {
              console.warn('[AuthService] Session restored but user data is invalid (missing role or username), logging out...');
              await this.clearSession();
              this.currentUser = null;
              this.logAuditEvent('SESSION_INVALID', 'Session restored but user data was invalid');
              return;
            }

            this.currentUser = session.user;
            // console.log('[AuthService] Session restored successfully for user:', session.user?.username);

            // Load tokens into cache for synchronous access (needed for DICOM Web client)
            // This ensures tokens are available immediately for DICOM Web API calls
            try {
              // Get auth token and populate cache
              let authToken: string | null = null;
              try {
                authToken = await this.secureStorage.getSessionItem('auth_token');
              } catch (error) {
                authToken = sessionStorage.getItem('auth_token');
                // Update cache directly if we got it from unencrypted storage
                if (authToken) {
                  this.secureStorage.tokenCache.authToken = authToken;
                }
              }

              // Get local token and populate cache
              let localToken: string | null = null;
              try {
                localToken = await this.secureStorage.getLocalItem('token');
              } catch (error) {
                localToken = localStorage.getItem('token');
                // Update cache directly if we got it from unencrypted storage
                if (localToken) {
                  this.secureStorage.tokenCache.localToken = localToken;
                }
              }

              // Update cache if tokens found
              if (authToken || localToken) {
                // console.log('[AuthService] Tokens loaded into cache for DICOM Web requests');
              } else {
                console.warn('[AuthService] No tokens found during session restoration');
              }
            } catch (tokenError) {
              console.warn('[AuthService] Failed to load tokens into cache:', tokenError);
            }

            // Get idle expiry - try encrypted first
            let storedIdleExpiry: number | null = null;
            try {
              const idleExpiryStr = await this.secureStorage.getSessionItem('idle_expiry');
              storedIdleExpiry = idleExpiryStr ? Number(idleExpiryStr) : null;
            } catch (error) {
              // Fallback to unencrypted
              storedIdleExpiry = Number(sessionStorage.getItem('idle_expiry')) || null;
            }

            const timeoutSeconds = storedIdleExpiry && storedIdleExpiry > 0
              ? storedIdleExpiry
              : this.SESSION_TIMEOUT_MINUTES * 60;

            this.sessionService.updateTimeoutSeconds(timeoutSeconds);
            this.sessionService.updateLastActivity(true);

            // Refresh session expiration asynchronously (don't block)
            this.refreshSessionExpiration(timeoutSeconds).catch(err => {
              console.warn('AuthService: Failed to refresh session expiration:', err);
            });

            this.logAuditEvent('SESSION_RESTORED', 'Session restored from storage');
          } else {
            // Session expired, clear it
            console.warn('[AuthService] Session expired, clearing...');
            await this.clearSession();
            this.logAuditEvent('SESSION_EXPIRED', 'Session expired during initialization');
          }
        } catch (parseError) {
          console.error('[AuthService] Failed to parse session data:', parseError);
          // Don't clear session on parse error - might be encrypted data we can't decrypt yet
          // The session will be checked again when encryption key is available
        }
      } else {
        // console.log('[AuthService] No session data found in storage');

        // If no session but we have tokens, try to restore session from tokens
        // This handles the case where old encrypted session was cleared but tokens still exist
        const hasToken = this.secureStorage.getCachedToken() ||
                        sessionStorage.getItem('auth_token') ||
                        localStorage.getItem('token');

        if (hasToken) {
          // console.log('[AuthService] No session found but tokens exist, attempting to restore session from API...');
          try {
            // Try to fetch user info from API using the token
            const userRoleData = await this.fetchUserRole();
            if (userRoleData && userRoleData.role) {
              // Create user object from API response
              // Use username from API if available, otherwise fallback to email prefix
              const user: User = {
                id: userRoleData.id || 'unknown',
                username: userRoleData.username || userRoleData.email?.split('@')[0] || 'user',
                email: userRoleData.email || '',
                role: userRoleData.role as 'admin' | 'radiologist' | 'technician' | 'viewer',
                permissions: userRoleData.permissions || [],
                lastLogin: new Date().toISOString(),
                failedLoginAttempts: 0,
                accountLocked: false,
              };

              // Validate user object has required fields
              if (!user.role || !user.username || user.username === 'user') {
                console.warn('[AuthService] User data from API is incomplete (missing role or valid username), logging out...');
                await this.clearSession();
                this.currentUser = null;
                this.logAuditEvent('SESSION_RESTORE_FAILED', 'User data from API was incomplete');
                return;
              }

              this.currentUser = user;

              // Get idle expiry from storage or use default
              let idleExpirySeconds: number | undefined;
              try {
                const idleExpiryStr = await this.secureStorage.getSessionItem('idle_expiry');
                idleExpirySeconds = idleExpiryStr ? Number(idleExpiryStr) : undefined;
              } catch (e) {
                idleExpirySeconds = Number(sessionStorage.getItem('idle_expiry')) || undefined;
              }

              // Recreate session with current user
              await this.createSession(user, idleExpirySeconds);
              // console.log('[AuthService] Session restored from API for user:', user.username);
              this.logAuditEvent('SESSION_RESTORED', 'Session restored from API after clearing old encrypted data');

              // Update session timeout
              const timeoutSeconds = idleExpirySeconds && idleExpirySeconds > 0
                ? idleExpirySeconds
                : this.SESSION_TIMEOUT_MINUTES * 60;
              this.sessionService.updateTimeoutSeconds(timeoutSeconds);
              this.sessionService.updateLastActivity(true);
            } else {
              console.warn('[AuthService] Could not restore session from API - token may be invalid or user data incomplete');
              // Clear session and logout if we can't get valid user data
              await this.clearSession();
              this.currentUser = null;
              this.logAuditEvent('SESSION_RESTORE_FAILED', 'Failed to restore session from API - invalid or incomplete user data');
            }
          } catch (apiError) {
            console.warn('[AuthService] Failed to restore session from API:', apiError);
            // If API call fails, token might be invalid - clear everything
            await this.clearSession();
            this.currentUser = null;
          }
        }
      }
    } catch (error) {
      console.error('[AuthService] Error checking existing session:', error);
      // Don't clear session on error - might be temporary encryption issue
      // Only clear if we're certain the session is invalid
    }
  }

  /**
   * Fetches user role from the API after successful authentication
   * @returns Promise with user role data or null if failed
   */
  private async fetchUserRole(): Promise<{ role: string; permissions?: string[]; email?: string; id?: string; username?: string } | null> {
    try {
      // Use AUTH_SERVICE_BASEURL since that's where login API is working
      const baseUrl = this.apiService.getAuthServiceUrl();
      const fullUrl = `${baseUrl}/users/me`;

      const response = await this.apiService.get<UserRoleResponse>(
        '/users/me',
        undefined,
        baseUrl
      );

      if (!response.success) {
        return null;
      }

      const data = response.data;

      if (data && data.is_success && data.data && data.data.length > 0) {
        const userData = data.data[0];
        return {
          role: userData.role,
          permissions: userData.permissions,
          email: userData.email,
          id: userData.id,
          username: userData.username,
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Changes the currently authenticated user's password.
   * Returns a success flag and message suitable for UI display.
   */
  public async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const baseUrl = this.apiService.getAuthServiceUrl();
      const endpoint = `/users/change-password?curr_password=${encodeURIComponent(
        currentPassword
      )}&new_password=${encodeURIComponent(newPassword)}`;
      const response = await this.apiService.post<any>(
        endpoint,
        undefined,
        baseUrl
      );

      if (!response.success) {
        return {
          success: false,
          message: response.error || 'Unable to change password. Please try again.',
        };
      }

      const data = response.data;

      if (data && (data.is_success === true || data.is_success === 'true')) {
        this.logAuditEvent('PASSWORD_CHANGED', 'User password changed successfully');
        return {
          success: true,
          message: data.message || 'Password changed successfully.',
        };
      }

      return {
        success: false,
        message: data?.message || 'Password change failed. Please verify your current password.',
      };
    } catch (error) {
      return {
        success: false,
        message: 'An unexpected error occurred while changing password.',
      };
    }
  }

  public async login(username: string, password: string): Promise<{ success: boolean; message: string; user?: User; isFirstTimeLogin?: boolean }> {
    const clientInfo = this.getClientInfo();

    try {
      // Use ApiService to make the login request
      // Pass empty Authorization header to prevent sending any existing token
      const response = await this.apiService.post<LoginResponse>(
        '/auth/login',
        {
          username,
          password,
        },
        this.apiService.getAuthServiceUrl(),
        {
          headers: {
            'Authorization': '' // Explicitly override to prevent sending old token
          }
        }
      );
      // Check if the API call was successful
      if (!response.success) {
        this.recordLoginAttempt(username, false, response.error || 'API request failed', clientInfo);
        return {
          success: false,
          message: response.error || 'Unable to connect to authentication server'
        };
      }

      const data = response.data;

      // Extract is_first_time_login from the response
      const userData = data?.data && data.data.length > 0 ? data.data[0] : null;
      const isFirstTimeLogin = userData?.user_data?.is_first_time_login === true;

      // Check if login was successful based on API response
      if (data && data.is_success && data.access_token) {
        var idleExpirySeconds = typeof data.idle_expiry === 'number' && !Number.isNaN(data.idle_expiry)
          ? Math.max(1, Math.floor(data.idle_expiry))
          : undefined;

        // Store idle expiry securely
        if (idleExpirySeconds) {
          await this.secureStorage.setSessionItem('idle_expiry', idleExpirySeconds.toString());
        } else {
          this.secureStorage.removeSessionItem('idle_expiry');
        }

        // Ensure encryption is ready before storing tokens
        try {
          const encryptionReady = await this.secureStorage.isEncryptionAvailable();
          if (!encryptionReady) {
            console.warn('[AuthService] Encryption not available - attempting to initialize...');
            // Try to initialize encryption
            await this.secureStorage.initializeEncryption();
          }

          // Store the authentication token securely (encrypted)
          // Store in both sessionStorage and localStorage for compatibility
          await this.secureStorage.setSessionItem('auth_token', data.access_token);
          await this.secureStorage.setLocalItem('token', data.access_token);

          // Store refresh token securely if provided
          if (data.refresh_token) {
            await this.secureStorage.setSessionItem('refresh_token', data.refresh_token);
          }
        } catch (storageError) {
          console.error('[AuthService] Failed to store tokens securely:', storageError);
          // Continue with login even if token storage fails - tokens might be stored unencrypted
          // This prevents login from failing due to encryption issues
        }

        // Keep backend "is_first_time_login" in plain sessionStorage for refresh-safe checks.
        // (User requested this to drive forced password change on reload.)
        try {
          sessionStorage.setItem(IS_FIRST_TIME_LOGIN_KEY, isFirstTimeLogin ? 'true' : 'false');
        } catch (e) {
          // ignore
        }

        // Persist forced password change requirement across refreshes.
        // If this is not a first-time login, clear any previously stored flags.
        try {
          if (isFirstTimeLogin) {
            await this.secureStorage.setSessionItem(FORCE_PASSWORD_CHANGE_KEY, '1');
          } else {
            this.secureStorage.removeSessionItem(FORCE_PASSWORD_CHANGE_KEY);
          }
          // Clear relogin-required on fresh login
          this.secureStorage.removeSessionItem(RELOGIN_REQUIRED_KEY);
        } catch (e) {
          // Fallback to unencrypted storage if secure storage fails
          if (isFirstTimeLogin) {
            sessionStorage.setItem(FORCE_PASSWORD_CHANGE_KEY, '1');
          } else {
            sessionStorage.removeItem(FORCE_PASSWORD_CHANGE_KEY);
          }
          sessionStorage.removeItem(RELOGIN_REQUIRED_KEY);
        }

        // Fetch user role from the API
        let userRoleData: { role: string; permissions?: string[]; email?: string; id?: string } | null = null;
        try {
          userRoleData = await this.fetchUserRole();
        } catch (fetchError) {
          console.warn('[AuthService] Failed to fetch user role from API:', fetchError);
          // Continue to fallback logic
        }

        if (!userRoleData || !userRoleData.role) {
          // If we can't get role from API, try to use data from login response
          // Note: userData is already extracted above

          if (!userData || !userData.role) {
            // If no role in login response either, use a default role based on username or allow login with viewer role
            // This handles cases where the API doesn't return role information
            console.warn('[AuthService] No role found in API response or login data, using default viewer role');

            const defaultUser: User = {
              id: username,
              username: username,
              email: userData?.email || '',
              role: 'viewer', // Default to viewer role if no role is provided
              permissions: [],
              lastLogin: new Date().toISOString(),
              failedLoginAttempts: 0,
              accountLocked: false,
            };

            try {
              this.currentUser = defaultUser;
              await this.createSession(defaultUser, idleExpirySeconds);
              this.recordLoginAttempt(username, true, 'Login successful (default role)', clientInfo);
              this.logAuditEvent('LOGIN_SUCCESS', `User ${username} logged in successfully with default viewer role`);

              return {
                success: true,
                message: data.message || 'Login successful',
                user: this.currentUser,
                isFirstTimeLogin
              };
            } catch (sessionError) {
              console.error('[AuthService] Failed to create session:', sessionError);
              // If session creation fails, still return success since tokens are stored
              // User can continue, session will be restored on next page load
              this.currentUser = defaultUser;
              return {
                success: true,
                message: data.message || 'Login successful',
                user: this.currentUser,
                isFirstTimeLogin
              };
            }
          }

          // Use role from login response as fallback
          const user: User = {
            id: userData.id || username,
            username: username,
            email: userData.email || '',
            role: userData.role as 'admin' | 'radiologist' | 'technician' | 'viewer',
            permissions: userData.permissions || [],
            lastLogin: new Date().toISOString(),
            failedLoginAttempts: 0,
            accountLocked: false,
          };

          try {
            this.currentUser = user;
            await this.createSession(user, idleExpirySeconds);
            this.recordLoginAttempt(username, true, 'Login successful (fallback role)', clientInfo);
            this.logAuditEvent('LOGIN_SUCCESS', `User ${username} logged in successfully with fallback role`);

            return {
              success: true,
              message: data.message || 'Login successful',
              user: this.currentUser,
              isFirstTimeLogin
            };
          } catch (sessionError) {
            console.error('[AuthService] Failed to create session:', sessionError);
            // If session creation fails, still return success since tokens are stored
            this.currentUser = user;
            return {
              success: true,
              message: data.message || 'Login successful',
              user: this.currentUser,
              isFirstTimeLogin
            };
          }
        }

        // Create user object from API role response
        const user: User = {
          id: userRoleData.id || username,
          username: username,
          email: userRoleData.email || '',
          role: userRoleData.role as 'admin' | 'radiologist' | 'technician' | 'viewer',
          permissions: userRoleData.permissions || [],
          lastLogin: new Date().toISOString(),
          failedLoginAttempts: 0,
          accountLocked: false,
        };

        try {
          this.currentUser = user;
          await this.createSession(user, idleExpirySeconds);
          this.recordLoginAttempt(username, true, 'Login successful', clientInfo);
          this.logAuditEvent('LOGIN_SUCCESS', `User ${username} logged in successfully with role: ${user.role}`);

          return {
            success: true,
            message: data.message || 'Login successful',
            user: this.currentUser,
            isFirstTimeLogin
          };
        } catch (sessionError) {
          console.error('[AuthService] Failed to create session:', sessionError);
          // If session creation fails, still return success since tokens are stored
          this.currentUser = user;
          return {
            success: true,
            message: data.message || 'Login successful',
            user: this.currentUser,
            isFirstTimeLogin
          };
        }
      } else {
        // Login failed
        const message = data?.message || 'Invalid username or password';
        this.recordLoginAttempt(username, false, message, clientInfo);
        return {
          success: false,
          message
        };
      }

    } catch (error) {
      console.error('[AuthService] Login error:', error);
      console.error('[AuthService] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });

      this.recordLoginAttempt(username, false, 'System error', clientInfo);

      // Provide more specific error message if possible
      let errorMessage = 'Unable to connect to authentication server. Please try again later.';
      if (error instanceof Error) {
        // If it's a network error, provide more specific message
        if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch')) {
          errorMessage = 'Network error: Unable to connect to authentication server. Please check your connection and try again.';
        } else if (error.message.includes('encrypt') || error.message.includes('Encryption')) {
          errorMessage = 'Login successful but failed to store session securely. Please try again.';
        } else {
          // Include the actual error message for debugging
          errorMessage = `Login error: ${error.message}. Please try again.`;
        }
      }

      // Don't automatically fall back to demo users
      // User should remain on login page if API fails
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  private async loginWithDemoUser(username: string, password: string, clientInfo: any): Promise<{ success: boolean; message: string; user?: User }> {
    // Fallback to demo users for development
    const user = this.DEMO_USERS.find(u => u.username === username);
    if (!user) {
      this.recordLoginAttempt(username, false, 'Invalid username', clientInfo);
      return { success: false, message: 'Invalid username or password' };
    }

    const isValidPassword = this.validatePassword(username, password);
    if (!isValidPassword) {
      this.recordLoginAttempt(username, false, 'Invalid password', clientInfo);
      return { success: false, message: 'Invalid username or password' };
    }

    // Successful demo login
    user.lastLogin = new Date().toISOString();
    this.currentUser = { ...user };
    await this.createSession(user);
    this.recordLoginAttempt(username, true, 'Demo login successful', clientInfo);
    this.logAuditEvent('LOGIN_SUCCESS', `Demo user ${username} logged in successfully`);

    return {
      success: true,
      message: 'Login successful (Demo Mode)',
      user: this.currentUser
    };
  }

  private validatePassword(username: string, password: string): boolean {
    // Demo password validation - In production, use proper password hashing
    const passwordMap: { [key: string]: string } = {
      'admin': 'admin123',
      'radiologist': 'radio123',
      'technician': 'tech123',
    };

    return passwordMap[username] === password;
  }

  private async createSession(user: User, idleExpirySeconds?: number): Promise<void> {
    const timeoutSeconds = idleExpirySeconds && idleExpirySeconds > 0
      ? idleExpirySeconds
      : this.SESSION_TIMEOUT_MINUTES * 60;
    const now = Date.now();
    const session = {
      user,
      expiresAt: now + (timeoutSeconds * 1000),
      createdAt: now,
      idleExpirySeconds: timeoutSeconds,
    };

    // Store session data securely (encrypted)
    await this.secureStorage.setSessionItem('ohif_session', JSON.stringify(session));
    await this.secureStorage.setSessionItem('idle_expiry', timeoutSeconds.toString());

    this.sessionService.updateTimeoutSeconds(timeoutSeconds);
    this.sessionService.updateLastActivity(true);
  }

  public async logout(): Promise<void> {
    try {
      const { autoSaveViewerDraftBeforeLogoutIfNeeded } = await import(
        '../utils/viewerLogoutAutoSave'
      );
      await autoSaveViewerDraftBeforeLogoutIfNeeded();
    } catch (error) {
      console.warn('[AuthService] Viewer auto-save before logout failed:', error);
    }

    if (this.currentUser) {
      this.logAuditEvent('LOGOUT', `User ${this.currentUser.username} logged out`);
    }

    // Call logout API to end session at DB level
    try {
      await this.apiService.post(
        'auth/logout',
        {},
        this.apiService.getAuthServiceUrl()
      );
    } catch (error) {
      // Continue with local logout even if API fails
    }

    await this.clearSession();
    this.currentUser = null;

    // Stop session timer
    this.sessionService.stopSessionTimer();

    // Don't redirect here - let the AuthContext handle navigation
    // This prevents double navigation and page reloads
  }

  private async clearSession(): Promise<void> {
    // Clear encrypted storage
    this.secureStorage.removeSessionItem('ohif_session');
    this.secureStorage.removeSessionItem('auth_token');
    this.secureStorage.removeSessionItem('refresh_token');
    this.secureStorage.removeSessionItem('idle_expiry');
    this.secureStorage.removeSessionItem(FORCE_PASSWORD_CHANGE_KEY);
    this.secureStorage.removeSessionItem(RELOGIN_REQUIRED_KEY);
    // Clear viewer/job specific session values on logout
    this.secureStorage.removeSessionItem('currentJobId');
    this.secureStorage.removeSessionItem('currentStudyUid');
    this.secureStorage.removeSessionItem('id');
    this.secureStorage.removeSessionItem('lastViewedStudyInstanceUIDs');
    this.secureStorage.removeSessionItem('currentSubmissionId');
    this.secureStorage.removeSessionItem('currentSubmissionProtocol');
    this.secureStorage.removeLocalItem('token');

    // Also clear unencrypted fallbacks
    sessionStorage.removeItem('ohif_session');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('idle_expiry');
    sessionStorage.removeItem(FORCE_PASSWORD_CHANGE_KEY);
    sessionStorage.removeItem(RELOGIN_REQUIRED_KEY);
    sessionStorage.removeItem(IS_FIRST_TIME_LOGIN_KEY);
    sessionStorage.removeItem('currentJobId');
    sessionStorage.removeItem('currentStudyUid');
    sessionStorage.removeItem('id');
    sessionStorage.removeItem('lastViewedStudyInstanceUIDs');
    sessionStorage.removeItem('currentSubmissionId');
    sessionStorage.removeItem('currentSubmissionProtocol');
    localStorage.removeItem('token');

    // Clear encryption key
    this.secureStorage.clearEncryptionKey();
  }

  /**
   * Returns true if the current session is required to change password before using the app.
   * This is persisted across page refreshes.
   */
  public async isForcePasswordChangeRequired(): Promise<boolean> {
    // Prefer the backend flag stored in plain sessionStorage (refresh-safe)
    const raw = sessionStorage.getItem(IS_FIRST_TIME_LOGIN_KEY);
    if (raw === 'true') {
      return true;
    }
    if (raw === 'false') {
      return false;
    }
    // Fallback to internal flag
    const value = await this.secureStorage.getSessionItem(FORCE_PASSWORD_CHANGE_KEY);
    return value === '1' || value === 'true';
  }

  /**
   * Returns true if the user must re-login (used after successful forced password change).
   */
  public async isReloginRequired(): Promise<boolean> {
    const value = await this.secureStorage.getSessionItem(RELOGIN_REQUIRED_KEY);
    return value === '1' || value === 'true';
  }

  public async setReloginRequired(required: boolean): Promise<void> {
    if (required) {
      await this.secureStorage.setSessionItem(RELOGIN_REQUIRED_KEY, '1');
      this.secureStorage.removeSessionItem(FORCE_PASSWORD_CHANGE_KEY);
      // Once password is changed, do not continue showing forced change modal on refresh
      sessionStorage.setItem(IS_FIRST_TIME_LOGIN_KEY, 'false');
    } else {
      this.secureStorage.removeSessionItem(RELOGIN_REQUIRED_KEY);
    }
  }

  public async clearPasswordChangeFlags(): Promise<void> {
    this.secureStorage.removeSessionItem(FORCE_PASSWORD_CHANGE_KEY);
    this.secureStorage.removeSessionItem(RELOGIN_REQUIRED_KEY);
    sessionStorage.removeItem(FORCE_PASSWORD_CHANGE_KEY);
    sessionStorage.removeItem(RELOGIN_REQUIRED_KEY);
    sessionStorage.removeItem(IS_FIRST_TIME_LOGIN_KEY);
  }

  public isAuthenticated(): boolean {
    // If currentUser is set, check session expiration
    if (this.currentUser !== null) {
      return !this.sessionService.isSessionExpired();
    }

    // If currentUser is not set yet (e.g., on page reload), check if session exists in storage
    // This handles the case where checkExistingSession() hasn't completed yet
    try {
      // Try to get session from encrypted storage (synchronously check if it exists)
      const encryptedSession = sessionStorage.getItem('encrypted_ohif_session');
      const unencryptedSession = sessionStorage.getItem('ohif_session');

      if (encryptedSession || unencryptedSession) {
        // Session data exists, try to parse it to check if it's valid
        const sessionData = unencryptedSession || (encryptedSession ? '{}' : null);
        if (sessionData) {
          try {
            const session = JSON.parse(sessionData);
            // Check if session is not expired
            if (session.expiresAt && session.expiresAt > Date.now()) {
              // Session exists and is valid, but not loaded yet
              // Return true to prevent redirect, session will be loaded by checkExistingSession()
              return true;
            }
          } catch (e) {
            // If it's encrypted, we can't parse it synchronously, but assume it's valid if it exists
            if (encryptedSession) {
              return true; // Assume valid if encrypted session exists
            }
          }
        }
      }

      // Also check for tokens as a fallback
      const hasToken = this.secureStorage.getCachedToken() ||
                       localStorage.getItem('token') ||
                       sessionStorage.getItem('auth_token');

      if (hasToken) {
        // Token exists, assume authenticated (session will be restored)
        return true;
      }
    } catch (error) {
      // If any error occurs, fall back to checking currentUser only
      console.warn('AuthService: Error checking session storage:', error);
    }

    return false;
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  public hasPermission(permission: string): boolean {
    if (!this.currentUser) return false;

    // Admin has all permissions
    if (this.currentUser.permissions.includes('*')) return true;

    return this.currentUser.permissions.includes(permission);
  }

  public hasRole(role: string): boolean {
    return this.currentUser?.role?.toLowerCase() === role.toLowerCase();
  }

  private getClientInfo() {
    return {
      ipAddress: '127.0.0.1', // In production, get from request headers
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
  }

  private recordLoginAttempt(username: string, success: boolean, reason: string, clientInfo: any): void {
    const attempt: LoginAttempt = {
      timestamp: clientInfo.timestamp,
      ipAddress: clientInfo.ipAddress,
      userAgent: clientInfo.userAgent,
      success,
      failureReason: success ? undefined : reason,
    };

    this.loginAttempts.push(attempt);

    // Keep only last 1000 attempts
    if (this.loginAttempts.length > 1000) {
      this.loginAttempts = this.loginAttempts.slice(-1000);
    }
  }

  private logAuditEvent(event: string, details: string): void {
    const auditLog = {
      timestamp: new Date().toISOString(),
      event,
      userId: this.currentUser?.id || 'unknown',
      username: this.currentUser?.username || 'unknown',
      ipAddress: '127.0.0.1', // In production, get from request
      userAgent: navigator.userAgent,
      details,
    };

    // In production, send to secure audit log service
  }

  public getLoginAttempts(): LoginAttempt[] {
    return [...this.loginAttempts];
  }

  public getSessionInfo() {
    return this.sessionService.getSessionInfo();
  }

  public updateLastActivity(force: boolean = false): void {
    this.sessionService.updateLastActivity(force);
  }

  private async refreshSessionExpiration(timeoutSeconds: number): Promise<void> {
    if (!timeoutSeconds || timeoutSeconds <= 0) {
      return;
    }

    let sessionData: string | null = null;
    try {
      sessionData = await this.secureStorage.getSessionItem('ohif_session');
    } catch (error) {
      // Fallback to unencrypted
      sessionData = sessionStorage.getItem('ohif_session');
    }

    if (!sessionData) {
      return;
    }

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();
      session.expiresAt = now + (timeoutSeconds * 1000);
      session.createdAt = now;
      session.idleExpirySeconds = timeoutSeconds;
      await this.secureStorage.setSessionItem('ohif_session', JSON.stringify(session));
    } catch (error) {
      // Failed to refresh session expiration
      console.error('AuthService: Failed to refresh session expiration:', error);
    }
  }

  private handleSessionTimeout(): void {
    void (async () => {
      await this.logout();
      window.dispatchEvent(new CustomEvent('session-timeout'));
    })();
  }
}

export default AuthService;
